import type { RadioChangeEvent } from 'antd'
import { Radio, Space } from 'antd'
import { Button } from 'components/atoms/Button/Button'
import { Modal } from 'components/atoms/Modal'
import type { Locality } from 'location/locality'
import { DADATA_TOKEN, localityFromAddress } from 'location/locality'
import { useLocality } from 'location/LocalityProvider'
import { useRef, useState } from 'react'
import type { DaDataAddress, DaDataSuggestion } from 'react-dadata'
import { AddressSuggestions } from 'react-dadata'

import { userAPI } from '../../axios/axios'
import { generalTheme } from '../../style'
import {
  SAnnotationText,
  SBodyWrapp,
  SLocation,
  SLocationBlock,
  SMainTitle,
  SRadioGroup,
  SSecondTitle,
  StyledDadataInput,
} from './Modal.styled'

export const LocationAddressModal = () => {
  const { locality, chooseLocality } = useLocality()
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState('manual')
  const [draft, setDraft] = useState<Locality | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const requestId = useRef(0)

  const close = () => {
    requestId.current += 1
    setOpen(false)
    setBusy(false)
  }

  const changeMode = (event: RadioChangeEvent) => {
    requestId.current += 1
    setMode(event.target.value)
    setDraft(null)
    setError('')
    setBusy(false)
  }

  const apply = async () => {
    requestId.current += 1
    const id = requestId.current
    setError('')
    setBusy(true)
    try {
      let selected = draft
      if (mode === 'automatic') {
        const position = await new Promise<GeolocationPosition>(
          (resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 10000,
            })
          },
        )
        const response = await userAPI.userLocation(
          position.coords.latitude,
          position.coords.longitude,
        )
        selected = response.data.data
      }
      if (id !== requestId.current) return
      if (!selected) throw new Error('Выберите населённый пункт из подсказок.')
      await chooseLocality(selected)
      close()
    } catch (exception) {
      if (id === requestId.current) {
        setError(
          mode === 'automatic'
            ? 'Не удалось определить населённый пункт. Выберите его вручную.'
            : 'Не удалось применить выбор. Выберите населённый пункт из подсказок и повторите.',
        )
      }
    } finally {
      if (id === requestId.current) setBusy(false)
    }
  }

  const clear = async () => {
    setBusy(true)
    try {
      await chooseLocality(null)
      close()
    } catch {
      setError('Не удалось сбросить местоположение. Повторите попытку.')
      setBusy(false)
    }
  }

  return (
    <>
      <SLocation
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ')
            event.currentTarget.click()
        }}
        onClick={() => {
          setDraft(locality)
          setMode('manual')
          setError('')
          setOpen(true)
        }}>
        {locality?.name || 'Моё местоположение'}
      </SLocation>
      {open && (
        <Modal
          open
          onClose={close}
          modalContentMaxWidth={generalTheme.maxWidthModal.default}>
          <SMainTitle>Где Вы находитесь?</SMainTitle>
          <SBodyWrapp>
            <SAnnotationText>
              Выбранный населённый пункт применяется к программам, мероприятиям,
              организациям и карте.
            </SAnnotationText>
            <SLocationBlock>
              <SMainTitle>Текущее местоположение</SMainTitle>
              <SSecondTitle>
                {locality?.name || 'Вся Ростовская область'}
              </SSecondTitle>
            </SLocationBlock>
            <SRadioGroup onChange={changeMode} value={mode} disabled={busy}>
              <Space direction="vertical">
                <Radio value="automatic">Автоматически</Radio>
                <Radio value="manual">Вручную</Radio>
              </Space>
            </SRadioGroup>
            <AddressSuggestions
              token={DADATA_TOKEN}
              defaultQuery={locality?.name || ''}
              filterLocations={[{ kladr_id: '61' }]}
              filterRestrictValue
              filterFromBound="city"
              filterToBound="settlement"
              onChange={(
                suggestion: DaDataSuggestion<DaDataAddress> | undefined,
              ) => {
                setDraft(
                  suggestion ? localityFromAddress(suggestion.data) : null,
                )
                setError('')
              }}
              inputProps={{
                disabled: mode !== 'manual' || busy,
                placeholder: 'Населённый пункт',
                onChange: () => setDraft(null),
              }}
              customInput={StyledDadataInput}
            />
            {error && <div role="alert">{error}</div>}
            <Button
              disabled={busy || (mode === 'manual' && !draft)}
              onClick={apply}>
              Применить
            </Button>
            <Button buttonStyle="textual" disabled={busy} onClick={clear}>
              Вся область — сбросить местоположение
            </Button>
          </SBodyWrapp>
        </Modal>
      )}
    </>
  )
}
